import { borders, colors, spaces } from 'settings';
import styled, { css } from 'styled-components';
import React from 'react';

export type ProgressBarSize = 'small' | 'large';

export interface ProgressBarProps {
    progress: number;
    variant?: string;
    size?: ProgressBarSize;
}

export const ProgressBarContainer = styled.div<{ size?: ProgressBarSize; variant?: string }>`
    position: relative;
    color: ${colors.puntRoad};
    background-color: ${colors.t1000};
    width: 100%;
    overflow: hidden;
    z-index: 1;

    ${({ size }) => css`
        height: ${size === 'small' ? spaces.space4 : spaces.space6}px;
        border-radius: ${size === 'small'
            ? borders.borderRadiuses.small
            : borders.borderRadiuses.medium}px;
    `}

    ${({ variant }) => {
        switch (variant) {
            case 'success':
                return css`
                    background-color: ${colors.mildredsCar};
                    color: ${colors.forrestLyf};
                `;
            case 'error':
                return css`
                    background-color: ${colors.littlePiggy};
                    color: ${colors.risingSun};
                `;
            case 'caution':
                return css`
                    background-color: ${colors.sandyButt};
                    color: ${colors.heritage};
                `;
            case 'soft':
                return css`
                    background-color: ${colors.pebbleBeach};
                    color: ${colors.puntRoad};
                `;
            case 'primary':
                return css`
                    background-color: ${colors.bleachedAs};
                    color: ${colors.puntRoad};
                `;
            default:
                return ``;
        }
    }};
`;

export const ProgressBarFill = styled.div<{
    size?: ProgressBarSize;
    progress: number;
}>`
    transition: width 0.3s ease-out;
    background-color: currentColor;
    height: 100%;

    ${({ progress }) => css`
        width: ${progress}%;
    `}

    ${({ size }) => css`
        border-radius: ${size === 'small'
            ? borders.borderRadiuses.small
            : borders.borderRadiuses.medium}px;
    `}
`;

const ProgressBar = ({ progress, variant, size }: ProgressBarProps) => {
    ProgressBarContainer.defaultProps = {
        role: 'progressbar',
        'aria-valuenow': progress,
        'aria-valuemin': 0,
        'aria-valuemax': 100,
    };

    return (
        <ProgressBarContainer size={size} variant={variant}>
            <ProgressBarFill size={size} progress={progress} />
        </ProgressBarContainer>
    );
};

export default ProgressBar;
